import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import Typography from '@material-ui/core/Typography'
import T from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import { IconButton } from '@material-ui/core'
import IconBack from '@material-ui/icons/ArrowBack'
import {
  jobWorkflowFetchFormData,
  jobWorkflowClearStateForm,
  jobWorkflowChange,
  jobWorkflowTriggeredBy,
} from 'reducers/jobsWorkflowAdminJob/reducer'
import { patchJobWorkflowInputs as jobWorkflowPreviewInputsChange } from 'reducers/jobInputsAdmin/reducer'
import { fetchSiteList } from 'reducers/logbookForm/reducer'
import JobWorkflowForm from 'routes/App/JobsAdmin/Jobs/JobWorkflowForm/JobWorkflowForm'
import withPromise from 'helpers/withPromise'
import Loader from 'components/Loading'
import { APP_LANGUAGES } from 'constants/index'
import ConfirmDialog from 'components/common/Popup/ConfirmDialog'
import Psfs from 'lib/psfs/Psfs'
import { siteFetchList } from 'reducers/site/reducer'
import messages from '../translate'

function JobsWorkflowAdminJobItem({
  isLoadForm,
  isLoadSiteList,
  formData,
  siteList,
  jobWorkflowChange,
  jobWorkflowFetchFormData,
  jobWorkflowClearStateForm,
  fetchSiteList,
  jobWorkflowPreviewInputsChange,
  jobWorkflowTriggeredBy,
  siteFetchList,
  history: { goBack },
  match: { params },
  intl: { formatMessage },
  licenseeId,
}) {
  const [isDialogOpened, setIsDialogOpened] = useState(false)
  const [sitesAffectedMessage, setSitesAffectedMessage] = useState('')
  const [submitData, setSubmitData] = useState({})

  const getEntityId = () => {
    const { id } = params

    return id !== 'create' ? id : false
  }

  const resetState = () => {
    setSitesAffectedMessage('')
    setIsDialogOpened(false)
    setSubmitData({})
  }

  useEffect(() => {
    const entityId = getEntityId()

    if (entityId) {
      jobWorkflowFetchFormData({ entityId })
    }

    return () => jobWorkflowClearStateForm()
  }, [params])

  useEffect(() => {
    const psfs = new Psfs()
    psfs
      .addFields(['id', 'name'])
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)

    siteFetchList({ psfs, licensee: licenseeId })
  }, [licenseeId])

  const onSaveWorkflow = (entityId, data, callback) => {
    withPromise(jobWorkflowChange, {
      entityId,
      data,
    }).then(errors => {
      if (callback) {
        callback()
      }

      return errors ? Promise.resolve(errors) : goBack()
    })
  }

  const onSubmit = (
    {
      title,
      enabled,
      position,
      sites,
      needs_deletion_reason,
      needs_deletion_reason_settings,
      automatic_due_date,
      automatic_due_date_days,
    },
    previewInputs,
    automaticDueDateTime,
  ) => {
    const entityId = getEntityId()

    Object.values(APP_LANGUAGES).forEach(({ key }) => {
      if (!title[key]) {
        // eslint-disable-next-line no-param-reassign
        title[key] = title.en
      }
    })

    const data = {
      title,
      enabled,
      position: Number(position),
      sites: sites.filter(site => site),
      needs_deletion_reason,
      automatic_due_date,
      automatic_due_date_days: automatic_due_date ? Number(automatic_due_date_days) : null,
      automatic_due_date_time: automatic_due_date ? moment(automaticDueDateTime).format('HH:mm:00') : null,
    }

    if (needs_deletion_reason) {
      data.needs_deletion_reason_settings = needs_deletion_reason_settings
    }
    if (!automatic_due_date) {
      data.automatic_due_date_days = null
      data.automatic_due_date_time = null
    }

    jobWorkflowPreviewInputsChange(previewInputs)

    if (entityId && sites.length) {
      const sitesToCheck = siteList
        .toJS()
        .filter(({ id: siteItemId }) =>
          sites.find(siteId => siteId === siteItemId),
        )
        .map(({ id }) => id)

      return withPromise(jobWorkflowTriggeredBy, {
        entityId,
        sites: sitesToCheck,
      }).then(response => {
        if (response.length) {
          const sitesNames = response.map(({ name }) => name).join(', ')

          setSitesAffectedMessage(sitesNames)
          setIsDialogOpened(true)
          setSubmitData({
            entityId,
            data,
          })
        } else {
          onSaveWorkflow(entityId, data)
        }
      })
    }

    return onSaveWorkflow(entityId, data)
  }

  return isLoadForm ? (
    <Loader pastDelay />
  ) : (
    <div className="container-fluid no-breadcrumb">
      <Typography variant="h5" className="headline">
        <IconButton size="medium" className="btn-back" onClick={goBack}>
          <IconBack fontSize="small" />
        </IconButton>
        <FormattedMessage {...messages.jobWorkflowList} />
      </Typography>
      <JobWorkflowForm
        formData={formData}
        onSubmit={onSubmit}
        siteList={siteList}
        fetchSiteList={fetchSiteList}
        isLoadSiteList={isLoadSiteList}
      />
      <ConfirmDialog
        open={isDialogOpened}
        title={formatMessage(messages.warning)}
        className="confirm-action dialog-primary-custom"
        onConfirmAction={() => {
          onSaveWorkflow(submitData.entityId, submitData.data, resetState)
        }}
        onClose={() => {
          resetState()
        }}
        message={formatMessage(messages.workflowSaveWarningMessage, {
          rules: <b>&quot;{sitesAffectedMessage}&quot;</b>,
        })}
        cancel={{
          color: 'default',
          variant: 'outlined',
          customText: formatMessage(messages.cancel),
        }}
        confirm={{
          color: 'scales-primary',
          variant: 'text',
          customText: formatMessage(messages.confirm),
        }}
      />
    </div>
  )
}

JobsWorkflowAdminJobItem.propTypes = {
  match: T.shape({
    params: T.shape({
      id: T.string,
    }),
  }).isRequired,
  history: T.shape({
    goBack: T.func,
  }).isRequired,
  formData: T.object.isRequired,
  isLoadForm: T.bool,
  jobWorkflowChange: T.func,
  jobWorkflowFetchFormData: T.func,
  jobWorkflowClearStateForm: T.func,
}

JobsWorkflowAdminJobItem.defaultProps = {
  isLoadForm: false,
  jobWorkflowChange: () => {},
  jobWorkflowFetchFormData: () => {},
  jobWorkflowClearStateForm: () => {},
}

export default connect(
  state => ({
    isLoadForm: state.getIn(['jobWorkflow', 'isLoadForm']),
    formData: state.getIn(['jobWorkflow', 'formData']).toJS(),
    isLoadSiteList: state.getIn(['logbookForm', 'isLoadSiteList']),
    siteList: state.getIn(['site', 'listData']),
    licenseeId: state.getIn(['profile', 'licenseeId']),
  }),
  {
    jobWorkflowFetchFormData,
    jobWorkflowPreviewInputsChange,
    jobWorkflowClearStateForm,
    jobWorkflowChange,
    fetchSiteList,
    jobWorkflowTriggeredBy,
    siteFetchList,
  },
)(injectIntl(JobsWorkflowAdminJobItem))
